import React, { useState } from 'react';
import { CompetencyRubricItem } from '../types';

interface RubricEditorProps {
  rubrics: Record<string, CompetencyRubricItem>;
  onChange: (rubrics: Record<string, CompetencyRubricItem>) => void;
}

const COMPETENCY_LIBRARY = [
  { id: 'VERIFICATION_COMPLIANCE', name: 'Verification & compliance', category: 'Compliance', desc: 'Verifies customer identity and adheres to strict compliance protocols.' },
  { id: 'CLARITY', name: 'Clarity', category: 'Customer care', desc: 'Explains complex terms and next steps clearly without jargon.' },
  { id: 'EMPATHY', name: 'Empathy', category: 'Customer care', desc: 'Acknowledges customer feelings promptly and warmly.' },
  { id: 'RESOLUTION', name: 'Resolution', category: 'Customer care', desc: 'Provides actionable, realistic resolution options and commitments.' },
  { id: 'PRODUCT_KNOWLEDGE', name: 'Product knowledge', category: 'Knowledge', desc: 'Demonstrates deep domain understanding and accurately answers policy questions.' },
  { id: 'ACTIVE_LISTENING', name: 'Active listening', category: 'Customer care', desc: 'Listens attentively without interrupting and reflects back customer needs.' },
  { id: 'OBJECTION_HANDLING', name: 'Objection handling', category: 'Sales', desc: 'Addresses customer hesitation or price concerns constructively.' },
  { id: 'UPSELLING', name: 'Upselling & Cross-selling', category: 'Sales', desc: 'Identifies contextual opportunities to offer relevant value additions.' },
];

export const RubricEditor: React.FC<RubricEditorProps> = ({ rubrics, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newWeight, setNewWeight] = useState<number>(20);
  const [newDesc, setNewDesc] = useState('');
  const [newCritical, setNewCritical] = useState<boolean>(false);

  const keys = Object.keys(rubrics);

  // Calculate total weight (handling both 0.20 and 20 formats)
  const rawSum = keys.reduce((acc, k) => acc + (rubrics[k]?.weight || 0), 0);
  const normalizedSum = rawSum <= 1.5 ? Math.round(rawSum * 100) : Math.round(rawSum);
  const isWeightValid = normalizedSum === 100;

  const handleWeightChange = (key: string, weightPct: number) => {
    const current = rubrics[key];
    if (!current) return;
    const isDecimalScale = Object.values(rubrics).some((r) => r.weight <= 1.0);
    const newWeightVal = isDecimalScale ? weightPct / 100 : weightPct;
    onChange({
      ...rubrics,
      [key]: {
        ...current,
        weight: newWeightVal,
      },
    });
  };

  const handleDescChange = (key: string, description: string) => {
    const current = rubrics[key];
    if (!current) return;
    onChange({
      ...rubrics,
      [key]: {
        ...current,
        description,
      },
    });
  };

  const handleToggleCritical = (key: string) => {
    const current = rubrics[key];
    if (!current) return;
    onChange({
      ...rubrics,
      [key]: {
        ...current,
        critical: !current.critical,
      },
    });
  };

  const handleAddFromLibrary = (item: typeof COMPETENCY_LIBRARY[0]) => {
    if (rubrics[item.id]) return;
    const isDecimalScale = Object.values(rubrics).some((r) => r.weight <= 1.0);
    const remainingWeight = Math.max(10, 100 - normalizedSum);
    const assignedWeight = isDecimalScale ? remainingWeight / 100 : remainingWeight;

    onChange({
      ...rubrics,
      [item.id]: {
        name: item.name,
        weight: assignedWeight,
        description: item.desc,
        critical: false,
      },
    });
  };

  const handleAddCompetency = () => {
    if (!newName.trim()) return;
    const generatedKey = (newKey.trim() || newName.trim().toUpperCase().replace(/[^A-Z0-9]/g, '_')) || `COMP_${Date.now()}`;
    const isDecimalScale = Object.values(rubrics).some((r) => r.weight <= 1.0);
    const assignedWeight = isDecimalScale ? newWeight / 100 : newWeight;

    onChange({
      ...rubrics,
      [generatedKey]: {
        name: newName.trim(),
        weight: assignedWeight,
        description: newDesc.trim() || 'Evaluates learner performance on this competency.',
        critical: newCritical,
      },
    });

    setNewKey('');
    setNewName('');
    setNewWeight(20);
    setNewDesc('');
    setNewCritical(false);
  };

  const handleRemoveCompetency = (key: string) => {
    const updated = { ...rubrics };
    delete updated[key];
    onChange(updated);
  };

  const handleRebalanceWeights = () => {
    if (keys.length === 0) return;
    const isDecimalScale = Object.values(rubrics).some((r) => r.weight <= 1.0);
    const equalWeight = isDecimalScale ? Number((1 / keys.length).toFixed(3)) : Math.round(100 / keys.length);
    const updated: Record<string, CompetencyRubricItem> = {};
    keys.forEach((k) => {
      updated[k] = {
        ...rubrics[k],
        weight: equalWeight,
      };
    });
    onChange(updated);
  };

  const handleRestoreDefaults = () => {
    onChange({
      VERIFICATION_COMPLIANCE: { name: 'Verification & compliance', weight: 0.30, description: 'Verifies customer identity and adheres to compliance.', critical: true },
      CLARITY: { name: 'Clarity', weight: 0.20, description: 'Speaks clearly and concisely in a professional tone.' },
      EMPATHY: { name: 'Empathy', weight: 0.20, description: 'Acknowledges customer feelings promptly and warmly.' },
      RESOLUTION: { name: 'Resolution', weight: 0.15, description: 'Provides realistic expectations and concrete next steps.' },
      PRODUCT_KNOWLEDGE: { name: 'Product knowledge', weight: 0.15, description: 'Accurately explains domain details and answers customer inquiries.' },
    });
  };

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', paddingBottom: '12px', borderBottom: '1px solid var(--line)' }}>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Competencies &amp; Weights</h4>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 0' }}>
            Choose what AI scores in this simulation and how much each competency counts. Learners see these on the Instructions page.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--muted)' }}>Total Weight:</span>
          <span
            className={`admin-status-badge ${isWeightValid ? 'admin-status-badge--active' : 'admin-status-badge--pending'}`}
            style={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'nowrap' }}
          >
            {normalizedSum}% {isWeightValid ? '✓ 100% Valid' : '⚠ Must add up to 100%'}
          </span>
          {keys.length > 0 && (
            <button
              type="button"
              onClick={handleRebalanceWeights}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ fontSize: '11.5px', padding: '3px 10px', minHeight: '30px' }}
            >
              ⚖ Split Evenly
            </button>
          )}
        </div>
      </div>

      {/* Competencies List */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {keys.length === 0 ? (
          <div style={{ padding: '24px', borderRadius: '10px', border: '1px dashed var(--line)', textAlign: 'center', fontSize: '13px', color: 'var(--muted)', background: 'var(--surface)', display: 'grid', gap: '12px', justifyItems: 'center' }}>
            <span>No competencies configured. At least one evaluation rubric is required to evaluate this simulation.</span>
            <button
              type="button"
              onClick={handleRestoreDefaults}
              className="admin-btn admin-btn--primary admin-btn--sm"
              style={{ width: 'fit-content' }}
            >
              + Restore Recommended Standard Rubrics (5 Competencies)
            </button>
          </div>
        ) : (
          keys.map((k) => {
            const item = rubrics[k];
            const displayWeight = item.weight <= 1.0 ? Math.round(item.weight * 100) : Math.round(item.weight);

            return (
              <div
                key={k}
                style={{
                  padding: '16px 18px',
                  borderRadius: '12px',
                  background: 'var(--surface)',
                  border: item.critical ? '1.5px solid #FCD34D' : '1px solid var(--line)',
                  display: 'grid',
                  gap: '10px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', minWidth: 0, flex: '1 1 auto' }}>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', wordBreak: 'break-word' }}>{item.name}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--muted)' }}>({k})</span>
                    {item.critical && (
                      <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '4px', border: '1px solid #FDE68A', whiteSpace: 'nowrap' }}>
                        ★ Critical Gate (Must Pass)
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: item.critical ? '#B45309' : '#64748B', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(item.critical)}
                        onChange={() => handleToggleCritical(k)}
                        style={{ accentColor: '#D97706' }}
                      />
                      Critical Gate
                    </label>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>Weight:</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        className="admin-form__input"
                        style={{ width: '64px', height: '32px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
                        value={displayWeight}
                        onChange={(e) => handleWeightChange(k, Number(e.target.value) || 0)}
                      />
                      <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 700 }}>%</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveCompetency(k)}
                      className="admin-btn admin-btn--danger admin-btn--sm"
                      style={{ padding: '4px 8px', minHeight: '32px' }}
                      title="Remove Competency"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Visual Weight Progress Indicator */}
                <div style={{ width: '100%', height: '6px', background: '#E2E8F0', borderRadius: '999px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(0, displayWeight))}%`,
                      height: '100%',
                      background: item.critical ? 'linear-gradient(90deg, #F59E0B, #EF4444)' : 'linear-gradient(90deg, #3B82F6, #6366F1)',
                      borderRadius: '999px',
                      transition: 'width 0.2s ease',
                    }}
                  />
                </div>

                <div>
                  <textarea
                    className="admin-form__textarea"
                    rows={2}
                    placeholder="Evaluation criteria, benchmark behaviors, and grading standard"
                    value={item.description}
                    onChange={(e) => handleDescChange(k, e.target.value)}
                    style={{ minHeight: '52px', fontSize: '13px' }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* More From Your Library Section */}
      <div style={{ padding: '16px 20px', borderRadius: '12px', background: '#F8FAFC', border: '1px solid #E2E8F0', display: 'grid', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', margin: 0 }}>
            📚 Add from Competencies Library
          </h5>
          <span style={{ fontSize: '11.5px', color: '#64748B' }}>Click any item to add to this scenario</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {COMPETENCY_LIBRARY.map((libItem) => {
            const isAdded = Boolean(rubrics[libItem.id]);
            return (
              <button
                key={libItem.id}
                type="button"
                onClick={() => !isAdded && handleAddFromLibrary(libItem)}
                disabled={isAdded}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: isAdded ? '1px solid #CBD5E1' : '1px solid #93C5FD',
                  background: isAdded ? '#F1F5F9' : '#FFFFFF',
                  color: isAdded ? '#94A3B8' : '#1D4ED8',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: isAdded ? 'default' : 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{isAdded ? '✓' : '+'}</span>
                <span>{libItem.name}</span>
                <span style={{ fontSize: '10px', opacity: 0.7 }}>({libItem.category})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Add Custom Competency Sub-form */}
      <div style={{ padding: '18px 20px', borderRadius: '12px', background: 'var(--white)', border: '1px solid var(--line)', display: 'grid', gap: '14px' }}>
        <h5 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', margin: 0 }}>
          + Add Custom Competency
        </h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '12px' }}>
          <div className="admin-form__group">
            <label className="admin-form__label">Competency Name *</label>
            <input
              type="text"
              className="admin-form__input"
              placeholder="e.g. Solution Negotiation, Regulatory Compliance"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div className="admin-form__group">
            <label className="admin-form__label">Weight (%) *</label>
            <input
              type="number"
              min="1"
              max="100"
              className="admin-form__input"
              value={newWeight}
              onChange={(e) => setNewWeight(Number(e.target.value) || 0)}
            />
          </div>
          <div className="admin-form__group">
            <label className="admin-form__label">Code Key (Optional)</label>
            <input
              type="text"
              className="admin-form__input"
              placeholder="e.g. SOLUTION_NEGOTIATION"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
          </div>
        </div>

        <div className="admin-form__group">
          <label className="admin-form__label">Evaluation Description / Criteria</label>
          <input
            type="text"
            className="admin-form__input"
            placeholder="e.g. Explains value transparently and secures customer alignment."
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={newCritical}
              onChange={(e) => setNewCritical(e.target.checked)}
              style={{ accentColor: '#D97706' }}
            />
            Mark as Critical Gate (Failing this fails the simulation attempt)
          </label>

          <button
            type="button"
            onClick={handleAddCompetency}
            disabled={!newName.trim()}
            className="admin-btn admin-btn--secondary"
          >
            + Add to Rubric
          </button>
        </div>
      </div>
    </div>
  );
};
